import { z } from 'zod';

import { auditSchema, positiveIntSchema } from './common';
import { teamIdSchema, tournamentIdSchema, uidSchema } from './ids';

/**
 * Normalises a team name into the key used to guarantee uniqueness.
 * Case, surrounding and repeated whitespace, and umlaut spelling must not be
 * enough to register a second "Löschzug 1".
 */
export function normalizeTeamName(name: string): string {
  return name
    .trim()
    .toLocaleLowerCase('de-DE')
    .replace(/\s+/g, ' ')
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');
}

/**
 * Structural rules for the walk-on song. The *policy* limit — how many seconds
 * a snippet may last — lives in the tournament settings and is applied by
 * {@link createWalkOnSongInputSchema}. Only the invariants that must hold for
 * any stored document are checked here.
 */
export const walkOnSongSchema = z
  .object({
    title: z.string().trim().min(1, 'Bitte den Einlaufsong angeben').max(120),
    artist: z.string().trim().max(120).optional(),
    sourceUrl: z.url('Bitte eine gültige Adresse angeben').max(500).optional(),
    startSeconds: z
      .number({ error: 'Bitte die Startsekunde angeben' })
      .int('Bitte eine ganze Zahl angeben')
      .min(0, 'Die Startsekunde darf nicht negativ sein')
      .max(36_000, 'Der Wert ist unrealistisch hoch'),
    endSeconds: z
      .number({ error: 'Bitte die Endsekunde angeben' })
      .int('Bitte eine ganze Zahl angeben')
      .min(1, 'Die Endsekunde muss mindestens 1 betragen')
      .max(36_000, 'Der Wert ist unrealistisch hoch'),
  })
  .refine((song) => song.endSeconds > song.startSeconds, {
    error: 'Die Endsekunde muss nach der Startsekunde liegen',
    path: ['endSeconds'],
  });

export type WalkOnSong = z.infer<typeof walkOnSongSchema>;

/**
 * Song schema for user input, extended by the maximum snippet length taken
 * from the tournament settings. No duration is hard-coded anywhere.
 */
export function createWalkOnSongInputSchema(maxDurationSeconds: number) {
  return walkOnSongSchema.refine(
    (song) => song.endSeconds - song.startSeconds <= maxDurationSeconds,
    {
      error: `Der Ausschnitt darf höchstens ${maxDurationSeconds} Sekunden lang sein`,
      path: ['endSeconds'],
    },
  );
}

export const teamStatusSchema = z.enum(['active', 'withdrawn']);
export type TeamStatus = z.infer<typeof teamStatusSchema>;

/** The fields a captain fills in when registering, and may edit afterwards. */
export const teamProfileSchema = z.object({
  fireDepartment: z.string().trim().min(2, 'Bitte die Feuerwehr angeben').max(80),
  name: z.string().trim().min(2, 'Bitte einen Teamnamen angeben').max(40),
  walkOnSong: walkOnSongSchema,
});

export type TeamProfile = z.infer<typeof teamProfileSchema>;

/**
 * A registered team as stored in `tournaments/{tournamentId}/teams/{teamId}`.
 *
 * The document is **public** (beamer and moderator views read it without a
 * login), so it deliberately carries no owner identifier. The captain's UID
 * lives only in the non-public {@link teamOwnerSchema} document — see
 * docs/SECURITY.md §6.3.
 */
export const teamSchema = teamProfileSchema.extend({
  id: teamIdSchema,
  tournamentId: tournamentIdSchema,
  /** Normalised name, mirrors the id of the uniqueness guard document. */
  nameKey: z.string().min(1),
  status: teamStatusSchema,
  /** Seeding position, assigned by the director before the draw. */
  seed: positiveIntSchema.nullable(),
  ...auditSchema.shape,
});

export type Team = z.infer<typeof teamSchema>;

/**
 * Ownership record at `tournaments/{tournamentId}/teamOwners/{teamId}`.
 *
 * The team → captain mapping that the security rules and the registration
 * function rely on. It is **not** public: only the owner and the tournament
 * director may read it. Keeping the UID here rather than on the public team
 * document is what closes docs/SECURITY.md §6.3. Written exclusively by the
 * Cloud Functions (Admin SDK); no client may create or edit it.
 */
export const teamOwnerSchema = z.object({
  captainUid: uidSchema,
});

export type TeamOwner = z.infer<typeof teamOwnerSchema>;

/**
 * Guard document at `tournaments/{tournamentId}/teamNames/{nameKey}`.
 * Firestore has no unique constraints; a document whose id *is* the normalised
 * name turns uniqueness into a create-if-absent rule. It holds only the team id
 * — no owner UID, so nothing personal leaks through this public index.
 */
export const teamNameClaimSchema = z.object({
  teamId: teamIdSchema,
});

export type TeamNameClaim = z.infer<typeof teamNameClaimSchema>;

/**
 * Second guard of the same kind, at
 * `tournaments/{tournamentId}/captains/{uid}`: it makes "one team per captain"
 * a create-if-absent rule and lets a captain find their own team by their UID.
 */
export const captainClaimSchema = z.object({
  teamId: teamIdSchema,
});

export type CaptainClaim = z.infer<typeof captainClaimSchema>;
